import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@material-ui/core/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';
import { UIIncidentHistory } from '../../../../../../../api/teamMetricsApi';

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(4),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
  },
  chartContainer: {
    height: 350,
    padding: theme.spacing(2),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#8E9299',
  },
}));

const SEVERITY_COLORS: Record<string, string> = {
  'SEV 1': '#F56565',
  'SEV 2': '#ED8936',
  'SEV 3': '#ECC94B',
  'SEV 4': '#9F7AEA',
  'TBD': '#A0AEC0',
};

const SEVERITY_ORDER = ['SEV 1', 'SEV 2', 'SEV 3', 'SEV 4', 'TBD'];

interface IncidentHistoryChartProps {
  data: UIIncidentHistory[];
  orgData?: UIIncidentHistory[];
  startDate: string;
}

function generateMonthRange(startDate: string): string[] {
  const months: string[] = [];

  const dateParts = startDate.split('-').map(Number);
  const startYear = dateParts[0];
  const startMonth = dateParts[1] - 1;

  const now = new Date();
  const endYear = now.getFullYear();
  const endMonth = now.getMonth();

  let year = startYear;
  let month = startMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month + 1).padStart(2, '0')}`);
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
  }

  return months;
}

export const IncidentHistoryChart: React.FC<IncidentHistoryChartProps> = ({ data, orgData, startDate }) => {
  const classes = useStyles();

  const chartData = useMemo(() => {
    const dataByMonth = new Map<string, UIIncidentHistory>();
    data.forEach((point) => {
      dataByMonth.set(point.date, point);
    });

    const orgDataByMonth = new Map<string, UIIncidentHistory>();
    if (orgData) {
      orgData.forEach((point) => {
        orgDataByMonth.set(point.date, point);
      });
    }

    const allMonths = generateMonthRange(startDate);

    return allMonths.map((month) => {
      const point = dataByMonth.get(month);
      const orgPoint = orgDataByMonth.get(month);

      const filteredCount = point?.incidentCount ?? 0;
      const orgCount = orgPoint?.incidentCount ?? 0;

      // Get severity breakdown from filtered data (prefixed with 'filtered_')
      const filteredSeverityCounts: Record<string, number> = {};
      const orgSeverityCounts: Record<string, number> = {};
      SEVERITY_ORDER.forEach((sev) => {
        const filteredSevCount = point?.severityDistribution?.find((s) => s.label === sev)?.count ?? 0;
        const orgSevCount = orgPoint?.severityDistribution?.find((s) => s.label === sev)?.count ?? 0;
        filteredSeverityCounts[`filtered_${sev}`] = filteredSevCount;
        orgSeverityCounts[`org_${sev}`] = orgSevCount;
      });

      return {
        date: month,
        org: orgCount,
        filtered: filteredCount,
        ...filteredSeverityCounts,
        ...orgSeverityCounts,
        percentage: orgCount > 0 ? Math.round((filteredCount / orgCount) * 100) : 0,
      };
    });
  }, [data, orgData, startDate]);

  const hasData = chartData.length > 0 && chartData.some((d) => d.org > 0 || d.filtered > 0);

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Incident History</Typography>
      </Box>

      <Paper className={classes.chartContainer} elevation={0}>
        {!hasData ? (
          <Box className={classes.emptyState}>
            <Typography>No incident history data available</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F1F1" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#8E9299' }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#8E9299' }}
                domain={[0, 'auto']}
                allowDecimals={false}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const dataPoint = payload[0]?.payload;
                  if (!dataPoint) return null;

                  const filtered = dataPoint.filtered ?? 0;
                  const org = dataPoint.org ?? 0;
                  const percentage = dataPoint.percentage ?? 0;

                  return (
                    <div style={{
                      backgroundColor: '#fff',
                      border: '1px solid #E2E8F0',
                      borderRadius: 4,
                      padding: '8px 12px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      fontSize: 12,
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{label}</div>
                      <div style={{ marginBottom: 8, fontWeight: 600 }}>
                        Total: {filtered}/{org} ({percentage}%)
                      </div>
                      {SEVERITY_ORDER.filter((sev) =>
                        (dataPoint[`filtered_${sev}`] > 0 || dataPoint[`org_${sev}`] > 0)
                      ).map((sev) => {
                        const filteredSev = dataPoint[`filtered_${sev}`] ?? 0;
                        const orgSev = dataPoint[`org_${sev}`] ?? 0;
                        const sevPercent = orgSev > 0 ? Math.round((filteredSev / orgSev) * 100) : 0;
                        return (
                          <div key={sev} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 2 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{
                                width: 8,
                                height: 8,
                                backgroundColor: SEVERITY_COLORS[sev],
                                borderRadius: 2,
                                display: 'inline-block',
                              }} />
                              <span>{sev}</span>
                            </div>
                            <span style={{ color: '#718096' }}>
                              {filteredSev}/{orgSev} ({sevPercent}%)
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, fontWeight: 600 }}
                iconType="square"
                iconSize={10}
                payload={[
                  ...SEVERITY_ORDER.filter((sev) =>
                    chartData.some((d) => (d as unknown as Record<string, number>)[`filtered_${sev}`] > 0 || (d as unknown as Record<string, number>)[`org_${sev}`] > 0)
                  ).map((sev) => ({
                    value: sev,
                    type: 'square' as const,
                    color: SEVERITY_COLORS[sev],
                  })),
                ]}
              />
              {/* Filtered bars (left side of each group) - render in reverse order so SEV 1 is on top */}
              {[...SEVERITY_ORDER].reverse().map((sev, index) => (
                <Bar
                  key={`filtered_${sev}`}
                  dataKey={`filtered_${sev}`}
                  stackId="filtered"
                  fill={SEVERITY_COLORS[sev]}
                  radius={index === SEVERITY_ORDER.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  name={`Filtered ${sev}`}
                >
                  {index === 0 && (
                    <LabelList
                      dataKey="filtered"
                      position="bottom"
                      offset={4}
                      fontSize={7}
                      fill="#718096"
                      formatter={() => 'Filtered'}
                    />
                  )}
                </Bar>
              ))}
              {/* Org/Total bars (right side of each group) - with opacity, render in reverse order so SEV 1 is on top */}
              {[...SEVERITY_ORDER].reverse().map((sev, index) => (
                <Bar
                  key={`org_${sev}`}
                  dataKey={`org_${sev}`}
                  stackId="org"
                  fill={SEVERITY_COLORS[sev]}
                  fillOpacity={0.4}
                  radius={index === SEVERITY_ORDER.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  name={`All ${sev}`}
                >
                  {index === 0 && (
                    <LabelList
                      dataKey="org"
                      position="bottom"
                      offset={4}
                      fontSize={7}
                      fill="#A0AEC0"
                      formatter={() => 'All'}
                    />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
};

export default IncidentHistoryChart;
